import React from "react";
import PolicyPage from "./PolicyPage";

const TOC = [
  { id: "what",       label: "What we collect" },
  { id: "why",        label: "Why we collect it" },
  { id: "third",      label: "Third parties" },
  { id: "cookies",    label: "Cookies" },
  { id: "rights",     label: "Your rights" },
  { id: "retention",  label: "Retention" },
  { id: "contact",    label: "Contact" },
];

export default function Privacy() {
  return (
    <PolicyPage
      testId="page-privacy"
      path="/privacy"
      title="Privacy Policy | TalesTalesTales"
      description="How TalesTalesTales collects, uses, retains and shares personal data. Written in plain English. GDPR/CCPA rights explained."
      eyebrow="Privacy"
      h1="Privacy policy"
      toc={TOC}
    >
      <p className="italic text-amber-100/60">
        Plain English summary: we collect the minimum needed to run your account and sagas. We do
        not sell personal data. You can request export or deletion at any time via the support page.
      </p>

      <section id="what">
        <h2 className="font-display text-2xl text-amber-50 mt-6 mb-3">What we collect</h2>
        <ul className="list-disc pl-5 space-y-2">
          <li><strong>Account data</strong> — email, display name, hashed password, tier.</li>
          <li><strong>Saga content</strong> — hero details, campaigns, chapters, Memory Codex entries.</li>
          <li><strong>Usage data</strong> — turn counts, model choice, timestamps, feature toggles.</li>
          <li><strong>Billing data</strong> — via Stripe. We never see or store card numbers.</li>
          <li><strong>Diagnostic logs</strong> — anonymised error traces via PostHog, plus IP-address-derived country for abuse prevention.</li>
        </ul>
      </section>

      <section id="why">
        <h2 className="font-display text-2xl text-amber-50 mt-6 mb-3">Why we collect it</h2>
        <p>
          To run your account, save your sagas, apply your turn quota, deliver AI narrator responses,
          bill you correctly if you subscribe, and detect abuse of the service. That&rsquo;s it.
          We do not train third-party AI models on your saga content, and we do not sell personal
          data to advertisers.
        </p>
      </section>

      <section id="third">
        <h2 className="font-display text-2xl text-amber-50 mt-6 mb-3">Third parties</h2>
        <ul className="list-disc pl-5 space-y-2">
          <li><strong>Anthropic, OpenAI, Google, OpenRouter</strong> — process turn prompts to generate narrator responses. Content sent is the minimum needed to render the next chapter.</li>
          <li><strong>ModelsLab</strong> — processes scene-illustration prompts.</li>
          <li><strong>Stripe</strong> — payment processing.</li>
          <li><strong>PostHog</strong> — anonymised product analytics.</li>
          <li><strong>Google Analytics / Google Ads</strong> — traffic measurement and remarketing (may be disabled via browser controls).</li>
        </ul>
      </section>

      <section id="cookies">
        <h2 className="font-display text-2xl text-amber-50 mt-6 mb-3">Cookies</h2>
        <p>
          We use a strictly-necessary session cookie for auth, plus optional analytics cookies from
          PostHog and Google. You can block them via your browser without breaking core play.
        </p>
      </section>

      <section id="rights">
        <h2 className="font-display text-2xl text-amber-50 mt-6 mb-3">Your rights</h2>
        <p>
          Under GDPR / UK GDPR / CCPA you can request access, export, correction, or deletion of
          your personal data. Email <a href="mailto:privacy@talestalestales.com" className="underline hover:text-amber-100">privacy@talestalestales.com</a>{" "}
          or open a ticket from the <a href="/support" className="underline hover:text-amber-100">support page</a>.
          We respond within 30 days.
        </p>
      </section>

      <section id="retention">
        <h2 className="font-display text-2xl text-amber-50 mt-6 mb-3">Retention</h2>
        <p>
          Active accounts retain saga content indefinitely. Deleted accounts are purged within 30
          days except where retention is required by law. Anonymised diagnostic logs are kept for
          up to 90 days.
        </p>
      </section>

      <section id="contact">
        <h2 className="font-display text-2xl text-amber-50 mt-6 mb-3">Contact</h2>
        <p>
          Privacy questions: <a href="mailto:privacy@talestalestales.com" className="underline hover:text-amber-100">privacy@talestalestales.com</a>.
          General support: <a href="/support" className="underline hover:text-amber-100">the support page</a>.
        </p>
      </section>
    </PolicyPage>
  );
}
